import Plausible from 'plausible-tracker';
import { hasAnalyticsConsent } from './consentState';

// Initialize Plausible tracker
const plausible = Plausible({
  domain: import.meta.env.VITE_PLAUSIBLE_DOMAIN || 'fullbook.app',
  apiHost: import.meta.env.VITE_PLAUSIBLE_API_HOST || 'https://plausible.io',
  trackLocalhost: import.meta.env.DEV,
});

/**
 * Initialize analytics tracking.
 * Called by consent bridge only when user has accepted analytics cookies.
 * Pageviews are tracked manually on route change (see AnalyticsPageViewTracker).
 */
export const initAnalytics = () => {
  if (import.meta.env.PROD) {
    console.log('[Analytics] Plausible ready (consent given)');
  } else {
    console.log('[Analytics] Running in dev mode - events will be logged only');
  }
};

/**
 * Track custom events. No-op if analytics consent not given.
 */
export const trackEvent = (
  eventName: string,
  props?: Record<string, string | number | boolean>
) => {
  if (!hasAnalyticsConsent()) return;
  if (import.meta.env.PROD) {
    plausible.trackEvent(eventName, { props });
  } else {
    console.log(`[Analytics] Event: ${eventName}`, props);
  }
};

/**
 * Manually track a page view. No-op if analytics consent not given.
 * Called by AnalyticsPageViewTracker on route change.
 */
export const trackPageview = (url?: string) => {
  if (!hasAnalyticsConsent()) return;
  if (import.meta.env.PROD) {
    plausible.trackPageview({}, { url });
  } else {
    console.log(`[Analytics] Pageview: ${url || window.location.pathname}`);
  }
};

/**
 * Convenience functions for common events
 * Use these throughout the app for consistent event tracking
 */
export const analytics = {
  // ============ Booking Events ============
  
  /**
   * Track when a user starts the booking process
   */
  bookingStarted: (activityId: string, activityName: string) => {
    trackEvent('Booking Started', { 
      activity_id: activityId, 
      activity_name: activityName 
    });
  },
  
  /**
   * Track when a booking is completed successfully
   */
  bookingCompleted: (bookingId: string, totalPrice: number, activityName: string) => {
    trackEvent('Booking Completed', { 
      booking_id: bookingId,
      revenue: totalPrice,
      activity_name: activityName
    });
  },
  
  /**
   * Track when a booking is cancelled
   */
  bookingCancelled: (bookingId: string, reason?: string) => {
    trackEvent('Booking Cancelled', { 
      booking_id: bookingId,
      ...(reason && { reason })
    });
  },
  
  // ============ Search Events ============
  
  /**
   * Track search queries
   */
  searchPerformed: (query: string, resultsCount: number, filters?: Record<string, any>) => {
    trackEvent('Search Performed', { 
      query, 
      results_count: resultsCount,
      ...(filters && { filters: JSON.stringify(filters) })
    });
  },
  
  /**
   * Track when a user clicks on a search result
   */
  searchResultClicked: (activityId: string, position: number) => {
    trackEvent('Search Result Clicked', {
      activity_id: activityId,
      position
    });
  },
  
  // ============ Auth Events ============
  
  /**
   * Track user sign up
   */
  userSignedUp: (method: string) => {
    trackEvent('User Signed Up', { method });
  },
  
  /**
   * Track user login
   */
  userLoggedIn: (method: string) => {
    trackEvent('User Logged In', { method });
  },
  
  /**
   * Track user logout
   */
  userLoggedOut: () => {
    trackEvent('User Logged Out');
  },
  
  // ============ Payment Events ============
  
  /**
   * Track when a payment is initiated
   */
  paymentInitiated: (amount: number, currency: string = 'EUR') => {
    trackEvent('Payment Initiated', { 
      revenue: amount,
      currency
    });
  },
  
  /**
   * Track when a payment is completed
   */
  paymentCompleted: (amount: number, method: string, currency: string = 'EUR') => {
    trackEvent('Payment Completed', { 
      revenue: amount,
      method,
      currency
    });
  },
  
  /**
   * Track when a payment fails
   */
  paymentFailed: (error: string, amount?: number) => {
    trackEvent('Payment Failed', { 
      error,
      ...(amount && { amount })
    });
  },
  
  // ============ Activity Events ============
  
  /**
   * Track when a user views an activity detail page
   */
  activityViewed: (activityId: string, activityName: string) => {
    trackEvent('Activity Viewed', {
      activity_id: activityId,
      activity_name: activityName
    });
  },
  
  /**
   * Track when a user adds an activity to favorites
   */
  activityFavorited: (activityId: string) => {
    trackEvent('Activity Favorited', {
      activity_id: activityId
    });
  },
  
  // ============ Group Vote Events ============
  
  /**
   * Track when a group vote is created
   */
  groupVoteCreated: (voteId: string, activityCount: number) => {
    trackEvent('Group Vote Created', {
      vote_id: voteId,
      activity_count: activityCount
    });
  },
  
  /**
   * Track when someone votes in a group vote
   */
  groupVoteSubmitted: (voteId: string, voterName: string) => {
    trackEvent('Group Vote Submitted', {
      vote_id: voteId,
      voter_name: voterName
    });
  },
  
  // ============ Contact Events ============
  
  /**
   * Track when a user submits a contact form
   */
  contactFormSubmitted: (reason: string) => {
    trackEvent('Contact Form Submitted', { reason });
  },
  
  // ============ Professional Events ============
  
  /**
   * Track when a professional signs up
   */
  professionalSignedUp: (businessName: string) => {
    trackEvent('Professional Signed Up', { business_name: businessName });
  },
  
  /**
   * Track when a professional creates their first listing
   */
  professionalFirstListing: (activityType: string) => {
    trackEvent('Professional First Listing', { activity_type: activityType });
  },
  
  // ============ Error Events ============
  
  /**
   * Track application errors that impact UX
   */
  errorOccurred: (errorType: string, errorMessage: string, page?: string) => {
    trackEvent('Error Occurred', {
      error_type: errorType,
      error_message: errorMessage,
      ...(page && { page })
    });
  },
};

export default plausible;
